import { DEFAULT_CURRENCY, IS_PRODUCTION, resolveUrlPath } from 'shared-utils'
import { getNetPrice, nexiOrderLineFactory } from 'api/utils/nexi/checkout/utils'
import { type NexiCreatePaymentRequest, type NexiCreatePaymentResponse } from 'ecosystem/nexi'
import { nexiApi } from 'api/utils/nexi'
import { runCreateOrder } from './runCreateOrder'
import {
  type BuildPayloadArgs,
  type CreateOrderContext,
  type CreateOrderHandlerOpts,
  type CreateOrderRouteResponse,
  type PaymentProviderAdapter
} from './types'

const buildNexiPayload = ({
  ctx,
  opts,
  backendOrder,
  configCountry,
  shippingInfo
}: BuildPayloadArgs): NexiCreatePaymentRequest => {
  const nexiOptions = opts.paymentProviderConfig?.providerOptions?.nexi
  const { items, cartDiscountCode } = ctx.payload
  const { orderLines, orderAmount } = nexiOrderLineFactory(
    items,
    cartDiscountCode,
    configCountry,
    opts.buyerVat
  )
  // protocol, host, port must be the same that was used for load nexi checkout widget
  const redirectDomain = IS_PRODUCTION ? opts.redirectDomain : 'http://localhost:3000'

  const payload = {
    order: {
      items: orderLines,
      amount: orderAmount,
      currency: nexiOptions?.purchaseCurrency || DEFAULT_CURRENCY,
      reference: backendOrder.orderNumber || ''
    },
    checkout: {
      integrationType: 'EmbeddedCheckout',
      url: resolveUrlPath(redirectDomain, '/checkout'),
      termsUrl: resolveUrlPath(redirectDomain, nexiOptions?.termsUrl ?? '/terms'),
      ...(nexiOptions?.merchantTermsUrl
        ? { merchantTermsUrl: resolveUrlPath(redirectDomain, nexiOptions.merchantTermsUrl) }
        : {}),
      appearance: {
        textOptions: {
          ...(nexiOptions?.completePaymentButtonText
            ? { completePaymentButtonText: nexiOptions.completePaymentButtonText }
            : {})
        },
        displayOptions: {
          showMerchantName: nexiOptions?.showMerchantName ?? true,
          showOrderSummary: nexiOptions?.showOrderSummary ?? true
        }
      },
      countryCode: configCountry || 'SWE',
      charge: true
    }
  } as NexiCreatePaymentRequest

  if (!shippingInfo) {
    return payload
  }

  return {
    ...payload,
    order: {
      ...payload.order,
      amount: payload.order.amount + shippingInfo.price,
      items: [
        ...payload.order.items,
        {
          reference: shippingInfo.id,
          name: shippingInfo.name,
          quantity: 1,
          unit: 'NA',
          // Not a mistake, and not the same convention as the product rows
          // above: Nexi carries the freight cost in the totals rather than in
          // `unitPrice`, and its own "add shipping cost" example sends
          // `unitPrice: 0` next to a populated `grossTotalAmount` /
          // `netTotalAmount`. So this is the one row where
          // `unitPrice * quantity !== netTotalAmount` — which is fine, because
          // `order.amount` is summed from `grossTotalAmount` and never from
          // `unitPrice`.
          // @see https://developer.nexigroup.com/nexi-checkout/en-EU/docs/add-shipping-cost/
          unitPrice: 0,
          taxRate: shippingInfo.tax_rate,
          taxAmount: shippingInfo.tax_amount,
          grossTotalAmount: shippingInfo.price,
          netTotalAmount: getNetPrice(shippingInfo.price, shippingInfo.tax_rate)
        }
      ]
    }
  } as NexiCreatePaymentRequest
}

/**
 * Nexi is the one market-locked provider: a session is bound to the configured
 * country, so the adapter exposes no `availableCountries` and the shopper's
 * pick is ignored — see `PaymentNexiDefault`.
 */
const nexiAdapter: PaymentProviderAdapter<NexiCreatePaymentResponse, NexiCreatePaymentRequest> = {
  name: 'NEXI',
  configCountry: (opts) => opts.paymentProviderConfig?.providerOptions?.nexi?.countryCode,
  orderLineFactory: nexiOrderLineFactory,
  buildPayload: buildNexiPayload,
  createSession: (payload) => nexiApi.checkout.createPayment(payload),
  respond: (nexiResponse) => ({ nexiResponse })
}

export const nexiPaymentProvider = (
  ctx: CreateOrderContext,
  opts: CreateOrderHandlerOpts
): Promise<CreateOrderRouteResponse> => runCreateOrder(ctx, opts, nexiAdapter)
